import { CompletionSource, snippetCompletion } from '@codemirror/autocomplete';
import { syntaxTree } from '@codemirror/language';
import { functions } from '@gosupersimple/penguino';

import { renderDocumentation, renderDescription } from './documentation';
import { getPenguinoState } from './state';
import { PenguinoTokenTree } from './parser';
import {
  getFunctionParameterBounds,
  getOuterFunctionToken,
  getTokenAtPos,
  isStringToken,
} from './traversal';

export const penguinoCompletions: CompletionSource = (context) => {
  const { fields, variables } = getPenguinoState(context.state);
  const tokens = syntaxTree(context.state)
    .children.filter(PenguinoTokenTree.isPenguinoTokenTree)
    .map((item) => item.token);

  const currentToken = getTokenAtPos(tokens, context.pos);
  const outerToken = getOuterFunctionToken(tokens, context.pos);
  const word = context.matchBefore(/\w[\w\s]*/);

  if ((word === null || word.text.length === 0) && !context.explicit) {
    return null;
  }

  if (outerToken?.lexeme === 'prop') {
    const { from, to } = getFunctionParameterBounds(tokens, context.pos);

    return {
      filter: !context.explicit,
      from,
      to,
      options: fields.map((field) =>
        snippetCompletion(`"${field.name}"#{1}`, {
          label: `"${field.name}"`,
          displayLabel: field.name,
          type: `property.${field.type}`,
        }),
      ),
    };
  }

  if (outerToken?.lexeme === 'variable') {
    const { from, to } = getFunctionParameterBounds(tokens, context.pos);

    return {
      filter: !context.explicit,
      from,
      to,
      options: variables.map((variable) =>
        snippetCompletion(`"${variable.key}"#{1}`, {
          label: `"${variable.key}"`,
          displayLabel: variable.key,
          type: `variable.${variable.type}`,
        }),
      ),
    };
  }

  if (currentToken !== null && isStringToken(currentToken)) {
    return null;
  }

  // For exact function or variable name matches, we want to rank them higher than properties.
  const hasExactFunction = Object.entries(functions).some(([name]) => name === word?.text);
  const hasExactVariable = variables.some(({ key }) => key === word?.text);

  return {
    from: word?.from ?? context.pos,
    to: word?.to ?? context.pos,
    options: Object.entries(functions)
      .map(([, f]) =>
        snippetCompletion(
          f.name === 'prop' || f.name === 'variable' ? `${f.name}("#{1}")` : `${f.name}(#{1})`,
          {
            label: f.name,
            type: 'function',
            info: () => renderDocumentation(f),
            section: { name: 'Functions', rank: hasExactFunction ? -1 : 1 },
          },
        ),
      )
      .concat(
        fields.map((field) =>
          snippetCompletion(`prop("${field.name}")#{1}`, {
            label: field.name,
            type: `property.${field.type}`,
            info: () => renderDescription(field),
            section: { name: 'Properties', rank: 0 },
          }),
        ),
      )
      .concat(
        variables.map((variable) =>
          snippetCompletion(`variable("${variable.key}")#{1}`, {
            label: variable.key,
            type: `variable.${variable.type}`,
            section: { name: 'Variables', rank: hasExactVariable ? -1 : 2 },
          }),
        ),
      ),
  };
};
